import Box from '@mui/material/Box';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import Dropdown from '../common/Dropdown';
import FormField from '../common/FormField';
import InputField from '../common/InputField';
import Modal from '../common/Modal';
import ModalFooter from '../common/ModalFooter';

export interface TypeInProductFormValues {
  productName: string;
  maker: number | string;
  category: number | string;
  type: number | string;
  unit: number | string;
  status: string;
}

interface MetaOption {
  value: number | string;
  label: string;
}

interface AddTypeInProductModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  control: Control<TypeInProductFormValues>;
  errors: FieldErrors<TypeInProductFormValues>;
  makersOptions: MetaOption[];
  categoriesOptions: MetaOption[];
  typesOptions: MetaOption[];
  unitsOptions: MetaOption[];
  statusOptions: MetaOption[];
  isSubmitting?: boolean;
  isFormValid?: boolean;
}

const AddTypeInProductModal: React.FC<AddTypeInProductModalProps> = ({
  open,
  onClose,
  onSubmit,
  control,
  errors,
  makersOptions = [],
  categoriesOptions = [],
  typesOptions = [],
  unitsOptions = [],
  statusOptions = [],
  isSubmitting = false,
  isFormValid = false,
}) => {
  const { t } = useTranslation();
  return (
    <Modal
      size="sm"
      open={open}
      onClose={onClose}
      title={t('typeInProducts.addTypeInProduct')}
      isTypeInModal={true}
      footer={
        <ModalFooter
          onCancel={onClose}
          onSubmit={onSubmit}
          disabled={isSubmitting || !isFormValid}
          isLoading={isSubmitting}
          submitText={
            isSubmitting
              ? t('typeInProducts.addingTypeInProduct')
              : isFormValid
                ? t('typeInProducts.addTypeInProduct')
                : t('form.fillAllFields')
          }
        />
      }
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            ml: 1,
            mt: 2,
            // gap: 2,
            overflowY: 'auto',
          }}
        >
          <FormField label={t('form.productName')} required>
            <Controller
              name="productName"
              control={control}
              rules={{ required: t('form.validation.productNameRequired') }}
              render={({ field }) => (
                <InputField
                  {...field}
                  value={field.value || ''}
                  placeholder={t('form.enterProductName')}
                  error={!!errors?.productName}
                  helperText={errors?.productName?.message as string}
                />
              )}
            />
          </FormField>

          <FormField label={t('form.maker')} required>
            <Controller
              name="maker"
              control={control}
              rules={{ required: t('form.validation.makerRequired') }}
              render={({ field }) => (
                <Dropdown
                  value={field.value ?? ''}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  options={makersOptions}
                  placeholder={t('form.selectMaker')}
                  error={!!errors?.maker}
                  helperText={errors?.maker?.message as string}
                />
              )}
            />
          </FormField>

          <FormField label={t('form.category')} required>
            <Controller
              name="category"
              control={control}
              rules={{ required: t('form.validation.categoryRequired') }}
              render={({ field }) => (
                <Dropdown
                  value={field.value ?? ''}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  options={categoriesOptions}
                  placeholder={t('form.selectCategory')}
                  error={!!errors?.category}
                  helperText={errors?.category?.message as string}
                />
              )}
            />
          </FormField>

          <FormField label={t('form.type')}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Dropdown
                  value={field.value ?? ''}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  options={typesOptions}
                  placeholder={t('form.selectType')}
                />
              )}
            />
          </FormField>

          <FormField label={t('form.unit')} required>
            <Controller
              name="unit"
              control={control}
              rules={{ required: t('form.validation.unitRequired') }}
              render={({ field }) => (
                <Dropdown
                  value={field.value ?? ''}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  options={unitsOptions}
                  placeholder={t('form.selectUnit')}
                  error={!!errors?.unit}
                  helperText={errors?.unit?.message as string}
                />
              )}
            />
          </FormField>

          <FormField label={t('common.status')} required>
            <Controller
              name="status"
              control={control}
              rules={{ required: t('form.validation.statusRequired') }}
              render={({ field }) => (
                <Dropdown
                  value={field.value ?? ''}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  options={statusOptions}
                  placeholder={t('common.selectStatus')}
                  hasSearch={false}
                  disabled={true}
                  error={!!errors?.status}
                  helperText={errors?.status?.message as string}
                />
              )}
            />
          </FormField>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddTypeInProductModal;
